/**
 * RaceTrace Live, Strategy: the focus car's battles with the class cars around it, from the live
 * strategy (live-strategy.ts) and each car's pace over its last PACE_LAPS green laps:
 *
 *   PUSH            catching the car ahead, and on its tail before the battle's horizon
 *   PUSH HARDER     catching too slowly (or not), but a pace it has run lately gets there
 *   OUT OF REACH    not this stint at any pace it has run lately
 *   DEFEND          the car behind catches it before the horizon: the pace that keeps it clear
 *   SAFE            the car behind is not catching it before the horizon
 *   PASSES IN PITS  the car ahead owes a stop more: ahead once it stops, no need to fight it
 *   NET RIVAL       the car behind owes a stop fewer: it is ahead after the stops; the pace to beat it
 *
 * Under a caution there are none: the field bunches and the gaps come back at the green.
 *
 * The horizon is the first of: the focus car's next stop, the rival's, the flag, BATTLE_MAX_LAPS. "On its tail" is
 * within CATCH_S. Every figure is an estimate from this race so far and moves with each lap.
 *
 * A target lap time is only ever one the car has run lately: never quicker than the average of
 * its RECENT_BEST fastest laps among its last RECENT_LAPS green laps (which is never quicker than
 * its best lap of the race). When a battle needs more than that, it says so (`beyondPace`) and
 * gives no lap time.
 */
import type { RaceChartData } from "@shared/types";
import { greenPace } from "./live-strategy";
import { PACE_LAPS } from "./live-gaps";
import { isGreenFlag } from "./flags";
import type { CarStrategy, LiveStrategy } from "./live-strategy";

/** On a car's tail: within this many seconds. */
export const CATCH_S = 1.0;
/**
 * A battle looks this many laps ahead at most: a gap's closing rate holds for a few laps, not a
 * stint (traffic, stops, drivers). On the 2026 development races a catch called within 15 laps
 * happened 68% (WRL) and 82% (WeatherTech) of the time for the car behind, against 57% and 75% with
 * no limit; beyond 15 laps under a third came true. The battle then runs to that lap ("lap 165").
 */
export const BATTLE_MAX_LAPS = 15;
/** Lately: its fastest laps among this many of its last green laps. */
const RECENT_LAPS = 15;
/** How many of those fastest laps its best recent pace averages. */
const RECENT_BEST = 3;

export type BattleKind = "push" | "push_harder" | "out_of_reach" | "defend" | "safe" | "passes_in_pits" | "net_rival";

export interface Battle {
  kind: BattleKind;
  /** The rival (chart key), ahead or behind on the road in class. */
  rival: number;
  side: "ahead" | "behind";
  /** Same team: shown, but marked. */
  teammate: boolean;
  /** Seconds between the two at the line now. */
  gapS: number;
  /** How fast the gap closes (s/lap; + it closes). */
  closingS: number | null;
  /** Laps until on its tail / caught, when that happens before the horizon. */
  inLaps: number | null;
  /** The lap that happens on (the focus car's laps). */
  onLap: number | null;
  /** Laps the battle has, and what ends it. */
  horizon: number;
  until: "your stop" | "its stop" | "the flag" | `lap ${number}`;
  /** The lap time to run (s): keep at or under it. */
  targetS: number | null;
  /** Its pace now (s/lap). */
  paceS: number | null;
  /** After both have stopped: seconds the focus car is ahead (+) or behind (−). */
  netS: number | null;
  /** The battle needs a lap time quicker than the car has run lately: no target is given. */
  beyondPace: boolean;
}

type Fcy = Set<number>;
export const fcyOf = (data: RaceChartData): Fcy => {
  const s = new Set<number>();
  for (const [a, b] of data.fcy ?? []) for (let l = a; l <= b; l++) s.add(l);
  return s;
};

/** A car's pace over its last PACE_LAPS green laps, and the best it has run lately (its 3 fastest of the last RECENT_LAPS). */
export function recentPace(data: RaceChartData, key: number, fcy = fcyOf(data)): { now: number | null; best: number | null } {
  const laps = data.cars[String(key)]?.laps ?? [];
  const now = greenPace(laps, data, fcy, PACE_LAPS);
  const xs: number[] = [];
  for (let i = laps.length - 1; i > 0 && xs.length < RECENT_LAPS; i--) {
    const l = laps[i];
    if (l.pit === 0 && laps[i - 1].pit === 0 && l.ltSec > 1 && l.ltSec < data.greenPaceCutoff && !fcy.has(l.l)) xs.push(l.ltSec);
  }
  const fast = [...xs].sort((a, b) => a - b).slice(0, RECENT_BEST);
  return { now, best: fast.length === RECENT_BEST ? fast.reduce((a, b) => a + b, 0) / RECENT_BEST : null };
}

/** Green laps used for a pace before a car has run enough for recentPace (its first lap left out: the start). */
const EARLY_PACE_LAPS = 2;

/**
 * Each car's pace for the track map (who catches the focus car, where a stop rejoins): its recent
 * pace (recentPace) once it has one; before that, early in a race or stint, its green laps since
 * its first (at least one); with none, its class's pace (the median of the class's cars that have
 * one). So a fast class closing on a slower one shows from the first laps.
 */
export function mapPaces(data: RaceChartData): Map<number, number | null> {
  const fcy = fcyOf(data);
  const own = new Map<number, number | null>();
  for (const k of Object.keys(data.cars)) {
    const key = Number(k);
    const now = recentPace(data, key, fcy).now;
    if (now !== null) {
      own.set(key, now);
      continue;
    }
    const laps = data.cars[k].laps;
    const xs: number[] = [];
    for (let i = laps.length - 1; i > 0 && xs.length < EARLY_PACE_LAPS; i--) {
      const l = laps[i];
      const prev = laps[i - 1];
      if (l.pit === 0 && prev.pit === 0 && l.ltSec > 1 && l.ltSec < data.greenPaceCutoff && prev.ltSec < data.greenPaceCutoff && !fcy.has(l.l) && (!l.flag || isGreenFlag(l.flag))) xs.push(l.ltSec);
    }
    own.set(key, xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : null);
  }
  const byClass = new Map<string, number[]>();
  for (const [key, p] of own) {
    if (p === null) continue;
    const cls = data.cars[String(key)].cls;
    byClass.set(cls, [...(byClass.get(cls) ?? []), p]);
  }
  const median = (xs: number[]) => {
    const s = [...xs].sort((a, b) => a - b);
    const m = s.length >> 1;
    return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
  };
  const out = new Map<number, number | null>();
  for (const [key, p] of own) {
    const cls = byClass.get(data.cars[String(key)].cls);
    out.set(key, p ?? (cls?.length ? median(cls) : null));
  }
  return out;
}

/**
 * A target lap time the car can run: `req` when it is no quicker than its best recent pace, else
 * none, with `beyond` when that is because it needs more pace (not because the pace is unknown).
 */
export function inReach(req: number | null, best: number | null): { targetS: number | null; beyondPace: boolean } {
  if (req === null || !Number.isFinite(req) || best === null) return { targetS: null, beyondPace: false };
  return req >= best ? { targetS: req, beyondPace: false } : { targetS: null, beyondPace: true };
}

/** Laps left in a car's stint before it must stop (its fuel left in green laps, without saving any); Infinity when it can reach the flag. */
function stintLeft(c: CarStrategy): number {
  if (c.stopsToGo === 0 || c.stintCap === null) return Infinity;
  return Math.max(0, c.stintCap - c.stintFuel);
}

const squash = (s: string) =>
  s
    .replace(/\s*\([^()]*\)\s*$/, "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

export function sameTeam(a: string, b: string): boolean {
  const x = squash(a);
  return !!x && x === squash(b);
}

/** The focus car's battles: the class car directly ahead and directly behind on the road. */
export function liveBattles(s: LiveStrategy, data: RaceChartData): Battle[] {
  if (!s.focus) return [];
  const f = s.focus.car;
  // Under a caution the field bunches up: no targets until the green.
  if (f.stopped || s.caution) return [];
  const running = s.cars.filter((c) => !c.stopped);
  const i = running.indexOf(f);
  const neighbours: [CarStrategy, "ahead" | "behind"][] = [];
  if (i > 0) neighbours.push([running[i - 1], "ahead"]);
  if (i >= 0 && i + 1 < running.length) neighbours.push([running[i + 1], "behind"]);
  const fcy = fcyOf(data);
  const fp = recentPace(data, f.num, fcy);
  const out: Battle[] = [];
  for (const [r, side] of neighbours) {
    const rp = recentPace(data, r.num, fcy);
    const gapS = Math.abs(r.gapS - f.gapS);
    // The laps to the flag: the leader's laps to go, as the focus car runs them (live-strategy lapsLeft).
    const lapsToFlag = f.lapsLeft ?? Infinity;
    const ends: [number, Battle["until"]][] = [
      [stintLeft(f), "your stop"],
      [stintLeft(r), "its stop"],
      [lapsToFlag, "the flag"],
      [BATTLE_MAX_LAPS, `lap ${f.laps + BATTLE_MAX_LAPS}`],
    ];
    const [horizonRaw, until] = ends.reduce((a, b) => (b[0] < a[0] ? b : a));
    const horizon = Number.isFinite(horizonRaw) ? Math.max(0, Math.floor(horizonRaw)) : 0;
    const netS = f.netGapS !== null && r.netGapS !== null ? r.netGapS - f.netGapS : null;
    const base: Battle = {
      kind: "safe",
      rival: r.num,
      side,
      teammate: sameTeam(f.team, r.team),
      gapS,
      closingS: null,
      inLaps: null,
      onLap: null,
      horizon,
      until,
      targetS: null,
      paceS: fp.now,
      netS,
      beyondPace: false,
    };
    if (fp.now === null || rp.now === null) continue;
    const owesF = f.stopsToGo ?? 0;
    const owesR = r.stopsToGo ?? 0;
    if (side === "ahead") {
      // Ahead on the road, but a stop more to make: the focus car is ahead once it stops.
      if (owesR > owesF && netS !== null && netS > 0) {
        out.push({ ...base, kind: "passes_in_pits" });
        continue;
      }
      const closing = rp.now - fp.now;
      const need = gapS - CATCH_S;
      const b = { ...base, closingS: closing };
      if (closing > 0 && need / closing <= horizon) {
        const n = Math.max(1, Math.ceil(need / closing));
        out.push({ ...b, kind: "push", inLaps: n, onLap: f.laps + n, targetS: fp.now });
        continue;
      }
      // The pace that catches it before the horizon, and whether it has run that lately.
      const req = horizon > 0 ? rp.now - need / horizon : null;
      const r = inReach(req, fp.best);
      out.push({ ...b, kind: r.targetS !== null ? "push_harder" : "out_of_reach", targetS: r.targetS, beyondPace: r.targetS === null });
      continue;
    }
    // Behind on the road, but a stop fewer to make: ahead of the focus car after the stops.
    if (owesR < owesF && netS !== null && netS < 0) {
      const lapsLeft = Number.isFinite(lapsToFlag) ? lapsToFlag : null;
      const deficit = -netS;
      // The pace that wins it back by the flag, only when the car has run it lately.
      out.push({ ...base, kind: "net_rival", ...inReach(lapsLeft ? rp.now - (deficit + CATCH_S) / lapsLeft : null, fp.best) });
      continue;
    }
    const closing = fp.now - rp.now;
    const room = gapS - CATCH_S;
    const b = { ...base, closingS: closing };
    if (closing > 0 && room / closing <= horizon) {
      const n = Math.max(1, Math.floor(Math.max(0, room) / closing));
      // The pace that keeps it clear until the horizon, only when the car has run it lately.
      out.push({ ...b, kind: "defend", inLaps: n, onLap: f.laps + n, ...inReach(horizon > 0 ? rp.now + room / horizon : rp.now, fp.best) });
      continue;
    }
    out.push({ ...b, kind: "safe" });
  }
  return out;
}
